/**
 * 保護付き添付ファイルのパスワード入力ダイアログ（成果物 / 対応内容で共用）。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-5
 *
 * 🔒 付きの添付は、ここで入力したパスワードをサーバが照合し、通った場合のみ
 * 5 分有効の署名付き URL が発行され、そのままダウンロードが始まる。
 *
 * ★ 手書きの `fixed inset-0 z-50` オーバーレイで実装してはいけない。
 *   モバイルでは下部タブバー (z-150) やサイドバー (z-100) がパスワード入力欄の
 *   上に描画され、クリックまで奪われる（2026-08-21 実害。BoardSubmissionList /
 *   BoardAttachmentSection の 2 箇所が 2 度のスイープで取り残された）。
 *   共通 Dialog はポータル + overlay 250 / content 260 で、背景色・ESC / 背景
 *   クリックでの閉じる挙動・重なり順がすべて 1 箇所（components/ui/dialog.tsx）
 *   から来る。重なり順の全体像は globals.css の .mobile-bottom-nav 付近を参照。
 */
import type { BoardAttachmentView } from '@goenhub/shared';
import { Lock } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { startDownload, useResolveAttachmentDownload } from '@/hooks/useBoardAttachments';

interface Props {
  postId: string;
  /** 入力対象の添付。null の間は閉じている。 */
  target: BoardAttachmentView | null;
  /** 閉じる（ダウンロード成功・「閉じる」・ESC / 背景クリック）。 */
  onClose: () => void;
  /** input の id。同一画面に 2 つ出しても衝突しないよう呼び出し側で分ける。 */
  inputId?: string;
}

export function AttachmentPasswordDialog({
  postId,
  target,
  onClose,
  inputId = 'attachment-password',
}: Props) {
  const resolveDownload = useResolveAttachmentDownload(postId);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setPassword('');
    setError(null);
    onClose();
  };

  const handleDownload = async () => {
    if (!target) return;
    if (!password.trim()) {
      setError('パスワードを入力してください');
      return;
    }
    setError(null);
    try {
      const res = await resolveDownload.mutateAsync({
        attachmentId: target.id,
        password,
      });
      if (res.status === 'ok') {
        startDownload(res.downloadUrl);
        close();
      } else if (res.status === 'wrong_password') {
        setError('パスワードが違います');
      } else if (res.status === 'file_missing') {
        setError('ファイルが見つかりません');
      } else {
        setError('ダウンロードできませんでした');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog
      open={!!target}
      onOpenChange={(open) => {
        // 照合中は ESC / 背景クリックで閉じない（結果の行き先が無くなるため）。
        if (!open && !resolveDownload.isPending) close();
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Lock className="size-4 text-[var(--accent-amber)]" />
            パスワードを入力
          </DialogTitle>
          <DialogDescription className="truncate">{target?.fileName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label htmlFor={inputId}>パスワード</Label>
          <Input
            id={inputId}
            type="password"
            value={password}
            autoFocus
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleDownload();
            }}
          />
          {error && <p className="text-[11px] text-[var(--accent-red)]">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={close}
            disabled={resolveDownload.isPending}
          >
            閉じる
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleDownload()}
            disabled={resolveDownload.isPending}
          >
            {resolveDownload.isPending ? '確認中…' : 'ダウンロード'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
